// src/services/clinicApi.ts
import { api } from "@/services/api";

export interface ClinicRequest {
  clinicName: string;
  clinicAddress: string;
  city: string;
  state: string;
  zipCode: string;
  contactPhone: string;
  status: "ACTIVE" | "INACTIVE" | "SUSPENDED";
  testTypeIds?: number[];

  // Configuration
  latitude?: number;
  longitude?: number;
  appointmentRequired?: boolean;
  serviceScope?: string;
  drugPanelAvailability?: string; // JSON
  panelAliasMapping?: string; // JSON
  clinicLevelPricing?: string; // JSON
  additionalFees?: string; // JSON
  collectionFee?: number;
  marginData?: string; // JSON
  cocRequired?: boolean;
  acceptsMedigenixForm?: boolean;
  operatingHours?: string; // JSON
  resultDeliveryOverride?: string;
  internalNotes?: string;
}

export interface Clinic extends ClinicRequest {
  id: number;
  vendorId: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
}

export const clinicApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Get clinics by vendor
    listClinicsByVendor: builder.query<Clinic[], number>({
      query: (vendorId) => `/super-admin/vendors/${vendorId}/clinics`,
      providesTags: ["Clinics"],
    }),

    // Get ALL clinics (for admin list)
    listAllClinics: builder.query<Clinic[], void>({
      query: () => `/super-admin/clinics`,
      providesTags: ["Clinics"],
    }),

    // Create clinic for vendor
    createClinic: builder.mutation<
      Clinic,
      { vendorId: number; data: ClinicRequest }
    >({
      query: ({ vendorId, data }) => ({
        url: `/super-admin/vendors/${vendorId}/clinics`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Clinics"],
    }),

    // Update clinic
    updateClinic: builder.mutation<
      Clinic,
      { id: number; data: Partial<ClinicRequest> }
    >({
      query: ({ id, data }) => ({
        url: `/super-admin/clinics/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Clinics"],
    }),

    // Delete clinic
    deleteClinic: builder.mutation<{ message: string }, number>({
      query: (id) => ({
        url: `/super-admin/clinics/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Clinics"],
    }),

    // Get clinic by ID
    getClinicById: builder.query<Clinic, number>({
      query: (id) => `/super-admin/clinics/${id}`,
      providesTags: ["Clinics"],
    }),

    // Search clinics
    searchClinics: builder.query<Clinic[], { zipCode: string; radius: number }>({
      query: ({ zipCode, radius }) => ({
        url: "/clinics/search",
        method: "GET",
        params: { zipCode, radius },
      }),
      providesTags: ["Clinics"],
    }),
  }),
});

export const {
  useListClinicsByVendorQuery,
  useListAllClinicsQuery,
  useCreateClinicMutation,
  useUpdateClinicMutation,
  useDeleteClinicMutation,
  useGetClinicByIdQuery,
  useSearchClinicsQuery,
  useLazySearchClinicsQuery,
} = clinicApi;
